import { formatAmount } from '@mitter-group/catalog';

/**
 * La moneda en que cobra un comercio: la de su país (`country/{id}.priceCurrency`, que carga el
 * admin en el panel). Plan `mitter-packages/docs/plans/2026-11-01-vitrina-ropa-fotos-moneda.md`.
 *
 * **Vendrá de `contracts` 0.15.0** (`IPriceCurrency`); hasta subir el paquete se lee
 * estructuralmente, con su propia validación, como la vitrina con `country.vat`.
 */
export interface PriceCurrency {
  /** ISO 4217, tres letras mayúsculas: `'PEN'`. */
  code: string;
  /** Lo que se pinta delante del monto: `'S/'`. De 1 a 4 caracteres, sin espacios alrededor. */
  symbol: string;
}

/** Ausente, `null` o inválida: dólares, que es lo que había antes. */
export const DEFAULT_PRICE_CURRENCY: Readonly<PriceCurrency> = Object.freeze({ code: 'USD', symbol: '$' });

/** Caracteres, no unidades de UTF-16: `€` es uno. */
function length(text: string): number {
  return [...text].length;
}

/**
 * Lee `priceCurrency` tal cual venga del documento. **No sale de `currencyCode`**: Venezuela
 * tiene `'VES'` y cobra en dólares.
 */
export function parsePriceCurrency(raw: unknown): PriceCurrency {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_PRICE_CURRENCY };
  }
  const { code, symbol } = raw as { code?: unknown; symbol?: unknown };
  const validCode = typeof code === 'string' && /^[A-Z]{3}$/.test(code);
  const validSymbol =
    typeof symbol === 'string' && symbol === symbol.trim() && length(symbol) >= 1 && length(symbol) <= 4;
  return validCode && validSymbol ? { code, symbol } : { ...DEFAULT_PRICE_CURRENCY };
}

/**
 * El monto con el formato de la vitrina (`formatAmount` de `catalog`, el mismo en todos los
 * países) y el símbolo delante: pegado si es de un carácter (`$89,90`), con un espacio si es más
 * largo (`S/ 89,90`).
 */
export function formatMoney(amount: number, symbol: string): string {
  const separator = length(symbol) === 1 ? '' : ' ';
  return `${symbol}${separator}${formatAmount(amount)}`;
}
